import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown, User, Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import NotificationDrawer from "./NotificationDrawer";
import ProfileDrawer from "./ProfileDrawer";

interface DashboardNavbarProps {
  onMenuToggle?: () => void;
}

export default function DashboardNavbar({ onMenuToggle }: DashboardNavbarProps) {
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "account">("profile");

  // ✅ Read from context — no extra /auth/me fetch
  const { user, clearAuth } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  return (
    <>
      <nav className="flex justify-between items-center w-full h-[62px] bg-[#EBF0FF] border-b border-[#E0E4F0] px-3 md:px-6 shadow-sm">

        {/* LEFT */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Mobile menu button */}
          <button
            onClick={onMenuToggle}
            className="md:hidden p-2 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>

          <div className="w-8 md:w-10 h-8 md:h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm md:text-base font-semibold">
            L
          </div>
          <div className="hidden sm:block">
            <h1 className="text-[13px] md:text-[15px] font-semibold">Launchpad</h1>
            <p className="text-xs text-gray-500">Capgemini Innovation</p>
          </div>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-3 md:gap-6">

          {/* NOTIFICATIONS */}
          <div className="relative cursor-pointer" onClick={() => setNotifOpen(true)}>
            <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
            <span className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full" />
          </div>

          {/* PROFILE DROPDOWN */}
          <div className="relative">
            <button onClick={() => setOpen(!open)} className="flex items-center gap-2">
              <div className="w-8 md:w-9 h-8 md:h-9 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="w-4 md:w-5 h-4 md:h-5 text-white" />
              </div>
              <span className="hidden sm:inline font-normal text-sm md:text-base">{user?.name ?? "User"}</span>
              <ChevronDown className="w-4 h-4 hidden sm:inline" />
            </button>

            {open && (
              <div className="absolute right-0 mt-4 w-56 md:w-64 bg-white rounded-xl shadow-xl py-4 z-50">
                <div className="flex items-center gap-3 px-4 pb-3 border-b">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>

                <button
                  onClick={() => { setOpen(false); setActiveTab("profile"); setProfileOpen(true); }}
                  className="flex w-full items-center gap-3 px-4 py-3 hover:bg-gray-50 text-sm"
                >
                  <User className="w-4 h-4 flex-shrink-0" /> <span className="truncate">My Profile</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 text-sm"
                >
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <ProfileDrawer
        open={profileOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onClose={() => setProfileOpen(false)}
      />
    </>
  );
}
